import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  HStack,
  Input,
  Tag as ChakraTag,
  TagLabel,
  TagCloseButton,
  Text,
  Button,
  useColorModeValue,
  useDisclosure,
  Portal,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  PopoverArrow,
  PopoverCloseButton,
  InputGroup,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { tagsAtom, createTagAtom, fetchTagsAtom, tagsLoadingAtom } from '../../../atoms/v0/tagAtoms';
import { MdAdd } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { TaskTag, Tag } from '../../../types/tag';

interface TagSelectorProps {
  selectedTags: TaskTag[];
  taskId: string;
  onAddTag: (tagId: string) => Promise<void>;
  onRemoveTag: (tagId: string) => Promise<void>;
  isDisabled?: boolean;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  taskId,
  onAddTag,
  onRemoveTag,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['v0/tasks', 'v0/common']);
  const [tags] = useAtom(tagsAtom);
  const isLoading = useAtomValue(tagsLoadingAtom);
  const fetchTags = useSetAtom(fetchTagsAtom);
  const createTag = useSetAtom(createTagAtom);

  const [newTagTitle, setNewTagTitle] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialFocusRef = useRef<HTMLInputElement>(null);

  // 背景和边框颜色
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tagBg = useColorModeValue('gray.100', 'gray.700');

  // 组件加载时获取标签列表
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // 创建新标签
  const handleCreateTag = async () => {
    const trimmedTitle = newTagTitle.trim();
    if (!trimmedTitle) return;

    // Remove leading # if present
    const finalTitle = trimmedTitle.startsWith('#') ? trimmedTitle.substring(1) : trimmedTitle;

    // Ensure final title is not empty after removing #
    if (!finalTitle) return;

    try {
      setIsCreatingTag(true);
      // Use finalTitle without leading #
      const success = await createTag({ title: finalTitle });

      if (success) {
        setNewTagTitle('');
      }
    } finally {
      setIsCreatingTag(false);
    }
  };

  // 添加已有标签到任务
  const handleAddExistingTag = async (tagId: string) => {
    await onAddTag(tagId);
    onClose();
  };

  // 过滤标签（排除已选标签和根据搜索查询过滤）
  const filteredTags = tags.filter(tag => {
    // 排除已选标签
    const isAlreadySelected = selectedTags.some(st => st.tagId === tag.id);
    if (isAlreadySelected) return false;

    // 根据搜索查询过滤
    if (searchQuery.trim() === '') return true;
    return tag.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Box>
      {/* 已选标签显示区 */}
      <Flex wrap="wrap" gap={2} mb={selectedTags.length > 0 ? 2 : 0}>
        {selectedTags.map((tag) => (
          <ChakraTag
            size="md"
            key={tag.tagId}
            borderRadius="full"
            variant="solid"
            colorScheme="teal"
            bg={tagBg}
            color={useColorModeValue('gray.800', 'gray.100')}
          >
            <TagLabel>{tag.title}</TagLabel>
            {!isDisabled && (
              <TagCloseButton
                onClick={() => onRemoveTag(tag.tagId)}
              />
            )}
          </ChakraTag>
        ))}
      </Flex>

      {/* 添加标签按钮 */}
      {!isDisabled && (
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          onOpen={onOpen}
          initialFocusRef={initialFocusRef}
          placement="bottom-start"
        >
          <PopoverTrigger>
            <Button
              size="sm"
              leftIcon={<MdAdd />}
              variant="outline"
              colorScheme="teal"
              borderRadius="full"
              isDisabled={isDisabled}
            >
              {t('v0/tasks:tags.addTag')}
            </Button>
          </PopoverTrigger>

          <Portal>
            <PopoverContent width="300px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader>{t('v0/tasks:tags.manageTags')}</PopoverHeader>

              <PopoverBody>
                <VStack spacing={3} align="stretch">
                  {/* 搜索已有标签 */}
                  <Box>
                    <Text fontSize="sm" mb={1}>{t('v0/common:search')}</Text>
                    <Input
                      ref={initialFocusRef}
                      placeholder={t('v0/tasks:tags.searchTags')}
                      size="sm"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Box>

                  {/* 创建新标签 */}
                  <Box>
                    <Text fontSize="sm" mb={1}>{t('v0/tasks:tags.createNew')}</Text>
                    <InputGroup size="sm">
                      <Input
                        placeholder={t('v0/tasks:tags.newTagPlaceholder')}
                        value={newTagTitle}
                        onChange={(e) => setNewTagTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateTag();
                          }
                        }}
                        isDisabled={isCreatingTag}
                      />
                      <InputRightElement width="4.5rem">
                        <Button
                          h="1.75rem"
                          size="xs"
                          colorScheme="teal"
                          onClick={handleCreateTag}
                          isLoading={isCreatingTag}
                          isDisabled={!newTagTitle.trim()}
                        >
                          {t('v0/common:create')}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </Box>

                  {/* 标签列表 */}
                  <Box>
                    <Text fontSize="sm" mb={1}>{t('v0/tasks:tags.existingTags')}</Text>
                    {isLoading ? (
                      <Flex justify="center" py={2}>
                        <Spinner size="sm" color="teal.500" />
                      </Flex>
                    ) : filteredTags.length > 0 ? (
                      <Flex wrap="wrap" gap={2} maxH="150px" overflowY="auto">
                        {filteredTags.map((tag) => (
                          <ChakraTag
                            key={tag.id}
                            size="md"
                            borderRadius="full"
                            variant="outline"
                            colorScheme="teal"
                            cursor="pointer"
                            onClick={() => handleAddExistingTag(tag.id)}
                            _hover={{ bg: 'teal.50', color: 'teal.800' }}
                          >
                            <TagLabel>{tag.title}</TagLabel>
                          </ChakraTag>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="sm" color="gray.500">{t('v0/tasks:tags.noTagsFound')}</Text>
                    )}
                  </Box>
                </VStack>
              </PopoverBody>

              <PopoverFooter>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onClose}
                >
                  {t('v0/common:close')}
                </Button>
              </PopoverFooter>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Box>
  );
};